import React from 'react';

import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  return (
    <section className="relative py-12 md:py-16 lg:py-24 overflow-hidden bg-[#0A0A0A]">
      {/* Background glows */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-[120px] opacity-10" style={{ background: '#00E5A0' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* LEFT: Text column */}
          <motion.div
            className="text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 md:mb-5"
              style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid rgba(0,229,160,0.3)' }}
              animate={{
                boxShadow: ['0 0 0 0 rgba(0,229,160,0.3)', '0 0 0 10px rgba(0,229,160,0)', '0 0 0 0 rgba(0,229,160,0)']
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Icon name="Zap" size={14} color="#00E5A0" />
              <span className="text-xs font-body font-medium" style={{ color: '#00E5A0' }}>🚀 Dé match tussen talent en kansen</span>
            </motion.div>

            <motion.h1
              className="font-heading font-bold text-white mb-4 md:mb-5 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-1" style={{ color: '#00E5A0' }}>VAKPERSONEEL GEZOCHT</span>
              <span className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-2 text-white">OF BESCHIKBAAR?</span>
              <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl" style={{ color: '#00E5A0' }}>BVM GROUP VERBINDT</span>
              <span className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white">BEDRIJVEN EN VAKMENSEN</span>
            </motion.h1>

            <motion.p
              className="text-sm sm:text-base md:text-lg mb-6 md:mb-7 max-w-lg mx-auto lg:mx-0 leading-relaxed"
              style={{ color: '#888888' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <strong className="text-white">Bent u werkgever?</strong> Wij leveren gecertificeerd EU-vakpersoneel — van chauffeur tot zorgmedewerker — binnen 72 uur.
              <br /><br />
              <strong className="text-white">Bent u professional?</strong> Wij plaatsen u bij de juiste opdrachtgever, in uw vakgebied, tegen eerlijk loon.
            </motion.p>

            <motion.div
              className="flex flex-wrap items-center gap-3 md:gap-4 justify-center lg:justify-start"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <div className="text-center lg:text-left">
                <p className="text-lg md:text-xl font-heading font-bold text-white">1.800+</p>
                <p className="text-xs font-body" style={{ color: '#666666' }}>Vakmensen beschikbaar</p>
              </div>
              <div className="w-px h-10" style={{ background: '#2A2A2A' }} />
              <div className="text-center lg:text-left">
                <p className="text-lg md:text-xl font-heading font-bold text-white">8.500+</p>
                <p className="text-xs font-body" style={{ color: '#666666' }}>Projecten ingevuld</p>
              </div>
              <div className="w-px h-10" style={{ background: '#2A2A2A' }} />
              <div className="text-center lg:text-left">
                <p className="text-lg md:text-xl font-heading font-bold text-white">4.8/5</p>
                <p className="text-xs font-body" style={{ color: '#666666' }}>Gemiddelde beoordeling</p>
              </div>
              <div className="w-px h-10" style={{ background: '#2A2A2A' }} />
              <div className="flex items-center gap-2 px-3 py-2 rounded-sm" style={{ background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.25)' }}>
                <Icon name="Users" size={14} color="#00E5A0" />
                <span className="text-xs font-body font-semibold" style={{ color: '#00E5A0' }}>EU-gecertificeerd</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-sm" style={{ background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.25)' }}>
                <Icon name="ShieldCheck" size={14} color="#00E5A0" />
                <span className="text-xs font-body font-semibold" style={{ color: '#00E5A0' }}>100% Compliant</span>
              </div>
            </motion.div>
          </motion.div>

          {/* RIGHT: Image column — visible on all screen sizes, stacked below text on mobile/tablet */}
          <motion.div
            className="relative flex flex-col gap-4 mt-6 lg:mt-0"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {/* Main image */}
            <div
              className="relative rounded-sm overflow-hidden"
              style={{ border: '1px solid #1A1A1A', boxShadow: '20px 20px 60px rgba(0,0,0,0.5), -5px -5px 30px rgba(0,229,160,0.05)' }}
            >
              <img
                src="/assets/images/Untitled-1774116312185.jpeg"
                alt="BVM Group vakpersoneel aan het werk"
                className="w-full object-contain"
              />

              {/* Floating badge top-right */}
              <div
                className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 rounded-sm"
                style={{ background: 'rgba(0,229,160,0.15)', border: '1px solid rgba(0,229,160,0.4)', backdropFilter: 'blur(8px)' }}
              >
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00E5A0' }} />
                <span className="text-xs font-body font-semibold" style={{ color: '#00E5A0' }}>Binnen 72 uur geplaatst</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;